export type ParseError = { line: number; message: string };

export type Shape = {
  id: string;
  label: string;
  type: string;
  line: number;
};

export type ArrowStyle = 'solid' | 'dashed' | 'dotted';

export type Arrow = {
  fromId: string;
  toId: string;
  style: ArrowStyle;
  bidir: boolean;
  label?: string;
  line: number;
};

export type Ast = {
  shapes: Shape[];
  arrows: Arrow[];
  errors: ParseError[];
};

export function isFreeDslCode(text: string): boolean {
  return /@startfree\b/.test(text || '');
}

export function parseFreeDsl(text: string): Ast {
  const errors: ParseError[] = [];
  const shapes: Shape[] = [];
  const arrows: Arrow[] = [];

  const lines = (text || '').split(/\r?\n/);
  const startIdx = lines.findIndex(l => /@startfree\b/.test(l));
  const endIdx = lines.findIndex(l => /@endfree\b/.test(l));

  if (startIdx < 0) {
    errors.push({ line: 1, message: 'missing @startfree' });
  }
  if (endIdx < 0) {
    errors.push({ line: lines.length, message: 'missing @endfree' });
  }

  const bodyStart = startIdx < 0 ? 0 : startIdx + 1;
  const bodyEnd = endIdx < 0 ? lines.length : endIdx;

  const ARROW_RE = /^(?:"([^"]+)"|(\S+))\s+(<-->|<-|<--|<\.\.|<->|-->|\.\.>|->)\s+(?:"([^"]+)"|(\S+))\s*(?::\s*(.+))?$/;

  type OpInfo = { style: ArrowStyle; bidir: boolean; reversed: boolean };
  const OPS: Record<string, OpInfo> = {
    '->':   { style: 'solid',  bidir: false, reversed: false },
    '-->':  { style: 'dashed', bidir: false, reversed: false },
    '..>':  { style: 'dotted', bidir: false, reversed: false },
    '<-':   { style: 'solid',  bidir: false, reversed: true  },
    '<--':  { style: 'dashed', bidir: false, reversed: true  },
    '<..':  { style: 'dotted', bidir: false, reversed: true  },
    '<->':  { style: 'solid',  bidir: true,  reversed: false },
    '<-->': { style: 'dashed', bidir: true,  reversed: false },
  };

  // Supports:
  //   TYPE ID
  //   TYPE "Quoted ID"
  //   TYPE ID as "Display Label"
  //   TYPE "Quoted ID" as "Display Label"
  const DECL_RE = /^([A-Za-z_][\w-]*)\s+(?:"([^"]+)"|([A-Za-z_][\w-]*))\s*(?:as\s+"([^"]+)")?\s*$/;

  for (let i = bodyStart; i < bodyEnd; i++) {
    const lineNo = i + 1;
    const raw = lines[i];
    const trimmed = raw.trim();
    if (trimmed === '' || trimmed.startsWith('#') || trimmed.startsWith("'")) continue;

    const arrowMatch = ARROW_RE.exec(trimmed);
    if (arrowMatch) {
      const src = arrowMatch[1] ?? arrowMatch[2]!;
      const op = arrowMatch[3];
      const dst = arrowMatch[4] ?? arrowMatch[5]!;
      const labelRaw = arrowMatch[6];
      const info = OPS[op];
      const fromId = info.reversed ? dst : src;
      const toId = info.reversed ? src : dst;
      const arrow: Arrow = {
        fromId, toId,
        style: info.style,
        bidir: info.bidir,
        line: lineNo,
      };
      if (labelRaw !== undefined) arrow.label = labelRaw.trim();
      arrows.push(arrow);
      continue;
    }

    const declMatch = DECL_RE.exec(trimmed);
    if (declMatch) {
      const type = declMatch[1];
      const quotedId = declMatch[2];
      const bareId = declMatch[3];
      const asLabel = declMatch[4];
      const id = quotedId ?? bareId!;
      const label = asLabel ?? id;
      shapes.push({ id, label, type, line: lineNo });
      continue;
    }

    errors.push({ line: lineNo, message: `unknown statement: ${trimmed}` });
  }

  return { shapes, arrows, errors };
}
